import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import React from 'react'

import PAGES from '../enums/pages'
import TIMER_MODE from '../enums/timerMode'

dayjs.extend(utc)
dayjs.extend(timezone)

const getPageTitle = slug => {
    const pageData = Object.values(PAGES).find(page => page.slug === slug)
    return pageData ? pageData.title : ''
}

const handleShortUrl = (allCourses, search) => {
    const luSlug = new URLSearchParams(search).get('lu')
    if (!luSlug) return

    for (const course of allCourses) {
        // Instead of looping through the transformed json
        // We can stringify and search for the slug
        const courseString = JSON.stringify(course)
        // Do a complete match with " to avoid partial match
        if (courseString.includes('"' + luSlug + '"')) {
            const learningPaths = [course.learning_path].concat(
                course.learning_path.optional_learning_paths
            )

            const nodes = learningPaths
                .map(lp => lp?.nodes ?? [])
                .reduce(function (pre, cur) {
                    return pre.concat(cur)
                })

            for (const node of nodes) {
                if (
                    node.unit?.slug &&
                    node.unit?.module?.slug &&
                    node.unit?.slug === luSlug
                ) {
                    window.location = [
                        window.location.origin,
                        'livebooks',
                        course.slug,
                        node.unit.module.slug,
                        node.unit.slug,
                    ].join('/')
                }
            }
        }
    }
}

const useScrollSpy = (ids, options) => {
    const [activeId, setActiveId] = React.useState()
    const observer = React.useRef()
    React.useEffect(() => {
        const elements = ids.map(id => document.getElementById(id))
        observer.current?.disconnect()
        observer.current = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry?.isIntersecting) {
                    setActiveId(entry.target.id)
                }
            })
        }, options)
        elements.forEach(el => {
            if (el) {
                observer.current?.observe(el)
            }
        })
        return () => observer.current?.disconnect()
    }, [ids, options])
    return { activeId, setActiveId }
}

const getTimerMode = (remainingTime, timeMultiplier) => {
    // If remaining time is less than 5mins
    if (remainingTime < 300 * timeMultiplier) {
        return TIMER_MODE.LT_5MIN
    } else if (
        remainingTime >= 300 * timeMultiplier &&
        remainingTime < 3600 * timeMultiplier
    ) {
        // If remaining time is between 59mins and 5mins
        return TIMER_MODE.GT_5MIN_LT_1HOUR
    } else {
        // If remaining time is above or equal to 1hr
        return TIMER_MODE.GT_1HOUR
    }
}

const b64toBlob = (b64Data, contentType = '', sliceSize = 512) => {
    var byteCharacters = atob(b64Data, 'base64')
    var byteArrays = []

    for (var offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        var slice = byteCharacters.slice(offset, offset + sliceSize)

        var byteNumbers = new Array(slice.length)
        for (var i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i)
        }

        var byteArray = new Uint8Array(byteNumbers)

        byteArrays.push(byteArray)
    }

    return new Blob(byteArrays, { type: contentType })
}

const openLinkInNewTab = link => {
    window.open(link, '_blank')
}

const getUtcDate = utcDate => {
    if (!utcDate) return 'Invalid date format'
    return dayjs.utc(utcDate)
}

const isProd = () => {
    return import.meta.env.VITE_REALM === 'prod'
}

/**
 * Conditionally applies dark mode classes only in development
 * In production, returns empty string to force light theme
 * @param {string} darkClasses - Tailwind dark mode classes (e.g., "dark:bg-gray-800")
 * @returns {string} - Dark classes if in dev, empty string if in prod
 */
const darkMode = (darkClasses) => {
    return isProd() ? '' : darkClasses
}

export {
    b64toBlob,
    darkMode,
    getPageTitle,
    getTimerMode,
    getUtcDate,
    handleShortUrl,
    isProd,
    openLinkInNewTab,
    useScrollSpy,
}
